// S14 — Denetim Kayıtları (Audit Log) listeleme + CSV dışa aktarımı.
//
// BE her mutasyon kontrolünde otomatik audit kaydı düşüyor. Bu sayfa
// `/api/v1/admin/audit-log` üzerinden okur, filtre uygular, detayları
// drawer'da gösterir ve `audit-log/export` üzerinden CSV indirir.

import { useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  DatePicker,
  Drawer,
  Empty,
  Grid,
  Input,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { Download, Eye } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@shared/hooks/useDebounce';
import { DataTable } from '@shared/components/DataTable';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { ROUTES } from '@shared/constants/routes';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { auditLogApi } from '@api/auditLog';
import {
  ACTION_KEYS,
  ENTITY_KEYS,
  actionColor,
  useActionLabel,
  useEntityLabel,
} from '@features/audit/labels';
import type { AuditLog, AuditLogEntry } from '@features/audit/types';

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

interface Filters {
  q: string;
  action: string | undefined;
  entityType: string | undefined;
  range: [Dayjs | null, Dayjs | null] | null;
}

/**
 * ISSUE-58 — `payload` is the already-parsed value emitted by BE
 * (`JsonElement?`). For `null`/`undefined` we render an em-dash placeholder
 * so the drawer still labels the section; for everything else we pretty-print
 * via `JSON.stringify(…, null, 2)` inside a `<pre>` (no dangerouslySetInnerHTML).
 */
function PayloadBlock({ title, payload }: { title: string; payload: unknown }) {
  const isEmpty = payload === null || payload === undefined;
  let pretty: string;
  if (isEmpty) {
    pretty = '—';
  } else if (typeof payload === 'string') {
    // Plain string payloads — render as-is, no double-JSON.
    pretty = payload;
  } else {
    try {
      pretty = JSON.stringify(payload, null, 2);
    } catch {
      // Last-ditch fallback (e.g. circular reference). We deliberately avoid
      // `String(payload)` here because it can emit '[object Object]'; instead
      // we list the keys so the audit drawer still surfaces something useful.
      pretty =
        typeof payload === 'object' && payload !== null
          ? `[unserializable object: keys=${Object.keys(payload).join(',')}]`
          : '[unserializable value]';
    }
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {title}
      </Text>
      <pre
        data-testid={`audit-payload-${title}`}
        style={{
          background: 'var(--ds-surface-2)',
          padding: 12,
          borderRadius: 8,
          fontSize: 12,
          maxHeight: 280,
          overflow: 'auto',
          fontFamily: 'var(--ds-font-mono, ui-monospace, monospace)',
          marginTop: 4,
          color: isEmpty ? 'var(--ds-text-tertiary, #94a3b8)' : undefined,
        }}
      >
        {pretty}
      </pre>
    </div>
  );
}

function MobileCard({ row, onOpen }: { row: AuditLogEntry; onOpen: () => void }) {
  const { t } = useTranslation(['audit', 'common']);
  const actionLabel = useActionLabel();
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 10,
        background: 'var(--ds-surface-1)',
        border: '1px solid var(--ds-border)',
        marginBottom: 8,
      }}
      data-testid="audit-card"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <Text strong>{row.actorName}</Text>
          <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)' }}>
            {dayjs(row.at).format('DD MMM YYYY HH:mm:ss')}
          </div>
        </div>
        <Tag color={actionColor(row.action)} style={{ marginInlineEnd: 0 }}>
          {actionLabel(row.action)}
        </Tag>
      </div>
      <Paragraph style={{ marginTop: 8, marginBottom: 4, fontSize: 13 }}>{row.summary}</Paragraph>
      <Button type="link" size="small" onClick={onOpen} style={{ paddingInline: 0 }}>
        {t('actions.openDetail')}
      </Button>
    </div>
  );
}

export function AuditLogPage() {
  const { t } = useTranslation(['audit', 'common', 'errors']);
  const actionLabel = useActionLabel();
  const entityLabel = useEntityLabel();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const actionOptions = useMemo(
    () =>
      ACTION_KEYS.map((value) => ({ value, label: actionLabel(value) })).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    [actionLabel],
  );
  const entityOptions = useMemo(
    () =>
      ENTITY_KEYS.map((value) => ({ value, label: entityLabel(value) })).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    [entityLabel],
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<Filters>({
    q: '',
    action: undefined,
    entityType: undefined,
    range: null,
  });
  const debouncedQ = useDebounce(filters.q, 350);
  const [detailId, setDetailId] = useState<string | null>(null);

  const query = {
    page,
    pageSize,
    userEmail: debouncedQ || undefined,
    action: filters.action,
    entityType: filters.entityType,
    fromUtc: filters.range?.[0]?.toISOString(),
    toUtc: filters.range?.[1]?.toISOString(),
  };

  const listQ = useQuery({
    queryKey: ['audit-log', query],
    queryFn: () => auditLogApi.list(query),
    placeholderData: (prev) => prev,
  });

  const detailQ = useQuery({
    queryKey: ['audit-log', 'detail', detailId],
    queryFn: () => auditLogApi.get(detailId ?? ''),
    enabled: !!detailId,
  });

  const handleExport = async () => {
    try {
      const blob = await auditLogApi.exportCsv(query);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${dayjs().format('YYYYMMDD-HHmmss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      void message.success(t('toast.exported'));
    } catch (err) {
      void message.error(resolveApiErrorMessage(err) || t('toast.exportFailed'));
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'at',
        title: t('table.columns.time'),
        dataIndex: 'at',
        width: 170,
        render: (v: string) => (
          <div style={{ lineHeight: 1.2 }}>
            <div>{dayjs(v).format('DD.MM.YYYY')}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(v).format('HH:mm:ss')}
            </Text>
          </div>
        ),
      },
      {
        key: 'actor',
        title: t('table.columns.actor'),
        dataIndex: 'actorName',
        width: 220,
        render: (_: unknown, row: AuditLogEntry) => (
          <Space>
            <Avatar
              size={28}
              style={{
                background: 'var(--ds-surface-2)',
                color: 'var(--ds-text-secondary)',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {(row.actorName ?? '?')
                .split(/[\s@.]/)
                .map((p) => p[0])
                .filter(Boolean)
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </Avatar>
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontWeight: 500 }}>{row.actorName}</div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {row.ip ?? t('system')}
              </Text>
            </div>
          </Space>
        ),
      },
      {
        key: 'action',
        title: t('table.columns.action'),
        dataIndex: 'action',
        width: 220,
        render: (a: string) => <Tag color={actionColor(a)}>{actionLabel(a)}</Tag>,
      },
      {
        key: 'resource',
        title: t('table.columns.resource'),
        dataIndex: 'resource',
        width: 160,
        render: (r: string, row: AuditLogEntry) => (
          <span>
            {entityLabel(r)}
            {row.resourceId ? (
              <Text type="secondary" style={{ marginInlineStart: 6, fontSize: 11 }}>
                #{row.resourceId.slice(0, 8)}
              </Text>
            ) : null}
          </span>
        ),
      },
      {
        key: 'summary',
        title: t('table.columns.summary'),
        dataIndex: 'summary',
      },
      {
        key: 'actions',
        title: '',
        width: 80,
        render: (_: unknown, row: AuditLogEntry) => (
          <Button
            size="small"
            type="text"
            icon={<Eye size={14} />}
            onClick={() => setDetailId(row.id)}
          >
            {t('common:actions.detail')}
          </Button>
        ),
      },
    ],
    [t, actionLabel, entityLabel],
  );

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const detail: AuditLog | undefined = detailQ.data;

  return (
    <div data-testid="audit-page">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={[
          { label: t('breadcrumb.home'), to: ROUTES.DASHBOARD },
          { label: t('breadcrumb.list') },
        ]}
        actions={
          <Button
            icon={<Download size={14} />}
            onClick={() => void handleExport()}
            data-testid="audit-export"
          >
            {t('actions.export')}
          </Button>
        }
      />

      <Card>
        <Space wrap style={{ width: '100%', marginBottom: 12 }}>
          <Input.Search
            placeholder={t('filters.searchPlaceholder')}
            value={filters.q}
            onChange={(e) => {
              setFilters((f) => ({ ...f, q: e.target.value }));
              setPage(1);
            }}
            allowClear
            style={{ minWidth: 220 }}
            data-testid="audit-search"
          />
          <Select
            allowClear
            placeholder={t('filters.actionPlaceholder')}
            options={actionOptions}
            value={filters.action}
            onChange={(v) => {
              setFilters((f) => ({ ...f, action: v }));
              setPage(1);
            }}
            style={{ minWidth: 200 }}
            showSearch
            optionFilterProp="label"
          />
          <Select
            allowClear
            placeholder={t('filters.resourcePlaceholder')}
            options={entityOptions}
            value={filters.entityType}
            onChange={(v) => {
              setFilters((f) => ({ ...f, entityType: v }));
              setPage(1);
            }}
            style={{ minWidth: 180 }}
            showSearch
            optionFilterProp="label"
          />
          <RangePicker
            value={filters.range}
            onChange={(v) => {
              setFilters((f) => ({ ...f, range: v }));
              setPage(1);
            }}
            showTime
          />
        </Space>

        {isMobile ? (
          listQ.isLoading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : items.length === 0 ? (
            <Empty description={t('empty')} />
          ) : (
            <div data-testid="audit-mobile-list">
              {items.map((row) => (
                <MobileCard key={row.id} row={row} onOpen={() => setDetailId(row.id)} />
              ))}
            </div>
          )
        ) : (
          <DataTable<AuditLogEntry>
            data={items}
            columns={columns}
            rowKey="id"
            loading={listQ.isFetching}
            pagination={{
              page,
              size: pageSize,
              total,
              onChange: (p, s) => {
                setPage(p);
                setPageSize(s);
              },
            }}
          />
        )}
      </Card>

      <Drawer
        title={detail ? actionLabel(detail.action) : t('drawer.title')}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        width={isMobile ? '100%' : 560}
        data-testid="audit-detail-drawer"
      >
        {detailQ.isLoading || !detail ? (
          <Skeleton active />
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div>
              <Text type="secondary">{t('drawer.time')}</Text>
              <div>{dayjs(detail.createdAt).format('DD MMM YYYY HH:mm:ss')}</div>
            </div>
            <div>
              <Text type="secondary">{t('drawer.actor')}</Text>
              <div>
                {detail.userEmail ?? t('system')}
                {detail.userId ? (
                  <Text type="secondary" style={{ marginInlineStart: 6, fontSize: 12 }}>
                    {detail.userId}
                  </Text>
                ) : null}
              </div>
            </div>
            <div>
              <Text type="secondary">{t('drawer.action')}</Text>
              <div>
                <Tag color={actionColor(detail.action)}>{actionLabel(detail.action)}</Tag>
              </div>
            </div>
            <div>
              <Text type="secondary">{t('drawer.resource')}</Text>
              <div>
                {entityLabel(detail.entityType)}
                {detail.entityId ? ` · ${detail.entityId}` : ''}
              </div>
            </div>
            {detail.correlationId ? (
              <div>
                <Text type="secondary">{t('drawer.correlationId')}</Text>
                <div style={{ fontFamily: 'var(--ds-font-mono, monospace)', fontSize: 12 }}>
                  {detail.correlationId}
                </div>
              </div>
            ) : null}
            {detail.ipAddress || detail.userAgent ? (
              <div>
                <Text type="secondary">{t('drawer.client')}</Text>
                <div style={{ fontSize: 12 }}>
                  {detail.ipAddress ?? '—'} · {detail.userAgent ?? '—'}
                </div>
              </div>
            ) : null}
            <PayloadBlock title={t('drawer.oldValues')} payload={detail.oldValuesJson} />
            <PayloadBlock title={t('drawer.newValues')} payload={detail.newValuesJson} />
            <PayloadBlock title={t('drawer.meta')} payload={detail.metadataJson} />
          </Space>
        )}
      </Drawer>
    </div>
  );
}
